import type { Menu } from "../service/menu/types";

export type DisplayPosition = "sidebar" | "avatarPopup";

export function parseDisplayPositions(
  value: string | undefined | null
): DisplayPosition[] {
  if (!value) return [];
  return value
    .split("|")
    .map((s) => s.trim())
    .filter((s): s is DisplayPosition => s === "sidebar" || s === "avatarPopup");
}

export function matchesDisplayPosition(
  menu: Menu.Menu,
  position: DisplayPosition
): boolean {
  const positions = parseDisplayPositions(menu.meta?.displayPosition);
  if (positions.length === 0) return position === "sidebar";
  return positions.includes(position);
}

export interface AvatarPopupGroup {
  root: Menu.Menu;
  items: Menu.Menu[];
}

export function partitionMenuTreeByPosition(menus: Menu.Menu[]): {
  sidebarTree: Menu.Menu[];
  avatarPopupMenus: Menu.Menu[];
  avatarPopupGroups: AvatarPopupGroup[];
  looseAvatarPopupItems: Menu.Menu[];
} {
  const menuTree = normalizeMenuTreeByParentId(menus);
  const isAvatarPopup = (m: Menu.Menu) => matchesDisplayPosition(m, "avatarPopup");

  const avatar: Menu.Menu[] = [];
  const groups: AvatarPopupGroup[] = [];
  const groupItemIds = new Set<number>();
  const seenLooseIds = new Set<number>();

  const sortBySort = (xs: Menu.Menu[]) =>
    [...xs].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

  const hasAvatarPopupDescendant = (node: Menu.Menu): boolean =>
    (node.children ?? []).some((child) => isAvatarPopup(child) || hasAvatarPopupDescendant(child));

  const collectIds = (node: Menu.Menu) => {
    if (node.id != null) groupItemIds.add(node.id);
    for (const child of node.children ?? []) collectIds(child);
  };

  const buildAvatarPopupTree = (nodes: Menu.Menu[]): Menu.Menu[] => {
    const out: Menu.Menu[] = [];
    for (const node of sortBySort(nodes).filter((m) => !m.hidden)) {
      const children = node.children?.length ? buildAvatarPopupTree(node.children) : [];
      if (isAvatarPopup(node) || children.length > 0) {
        out.push({ ...node, children });
      }
    }
    return out;
  };

  const isProfileRoot = (node: Menu.Menu) =>
    node.path === "/profile" ||
    node.component === "pages/profile/Profile" ||
    node.name === "个人中心" ||
    node.meta?.title === "个人中心";

  const visitForAvatar = (node: Menu.Menu) => {
    if (node.children?.length) {
      const canBeGroupRoot = isAvatarPopup(node) || isProfileRoot(node);
      if (canBeGroupRoot && hasAvatarPopupDescendant(node)) {
        const items = buildAvatarPopupTree(node.children);
        if (items.length > 0) {
          groups.push({ root: node, items });
          for (const it of items) collectIds(it);
        }
        return;
      }
      for (const c of node.children) visitForAvatar(c);
      return;
    }
    if (isAvatarPopup(node) && (node.id == null || !seenLooseIds.has(node.id))) {
      avatar.push(node);
      if (node.id != null) seenLooseIds.add(node.id);
    }
  };

  for (const m of menuTree) visitForAvatar(m);

  const sidebarTree = buildSidebarTree(menuTree);

  const looseAvatarPopupItems = sortBySort(
    avatar.filter((m) => m.id == null || !groupItemIds.has(m.id))
  );

  return {
    sidebarTree,
    avatarPopupMenus: sortBySort(avatar),
    avatarPopupGroups: groups.map((g) => ({
      root: g.root,
      items: sortBySort(g.items),
    })),
    looseAvatarPopupItems,
  };
}

function normalizeMenuTreeByParentId(menus: Menu.Menu[]): Menu.Menu[] {
  const nodeMap = new Map<number, Menu.Menu>();
  const roots: Menu.Menu[] = [];

  const cloneNode = (node: Menu.Menu): Menu.Menu => {
    const cloned: Menu.Menu = {
      ...node,
      children: node.children?.map(cloneNode) ?? [],
    };
    if (cloned.id != null && !nodeMap.has(cloned.id)) {
      nodeMap.set(cloned.id, cloned);
    }
    return cloned;
  };

  for (const menu of menus) roots.push(cloneNode(menu));

  const rootIds = new Set(roots.map((node) => node.id).filter((id): id is number => id != null));
  const attachedIds = new Set<number>();

  for (const node of nodeMap.values()) {
    const parentId = node.parentId;
    if (node.id == null || parentId == null || parentId === 0) continue;
    const parent = nodeMap.get(parentId);
    if (!parent || parent === node) continue;

    const alreadyChild = parent.children?.some((child) => child.id === node.id);
    if (!alreadyChild) {
      parent.children = [...(parent.children ?? []), node].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
    }
    attachedIds.add(node.id);
  }

  return roots.filter((node) => node.id == null || !attachedIds.has(node.id) || !rootIds.has(node.id));
}

/**
 * 递归构造侧栏树：保留 DIR 嵌套，但仅保留：
 * 1. displayPosition='sidebar'（默认）的叶子
 * 2. 至少有一个 sidebar 后代的 DIR
 */
function buildSidebarTree(menus: Menu.Menu[]): Menu.Menu[] {
  const out: Menu.Menu[] = [];
  for (const node of sortVisible(menus)) {
    const isSidebarNode = matchesDisplayPosition(node, "sidebar");
    const children = node.children?.length ? buildSidebarTree(node.children) : [];

    if (node.children?.length) {
      if (children.length > 0) {
        out.push({ ...node, children });
      } else if (isSidebarNode) {
        out.push({ ...node, children: [] });
      }
    } else if (isSidebarNode) {
      out.push({ ...node, children: [] });
    }
  }
  return out;
}

function sortVisible(menus: Menu.Menu[]): Menu.Menu[] {
  return [...menus]
    .filter((m) => !m.hidden)
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
}
